import { useEffect, useRef, useState } from "react";

const STORAGE_KEY = "mmntm:operator_hint_shown";
const DISPLAY_MS = 2800;

/**
 * One-shot hint nudging users to try the `:` operator picker.
 *
 * Renders nothing until the attached input gains focus for the first
 * time on this user's machine; then displays a small pulsing pill near
 * the input for ~2.8s, fading out cleanly. Localstorage gate ensures
 * it never reappears, regardless of which input first triggered it
 * (search page or command palette).
 *
 * Pure CSS-variable styling so it follows the user's active theme.
 *
 * @param {object} props
 * @param {React.RefObject<HTMLElement>} props.inputRef
 *   Required — the picker input we're hinting about.
 * @param {"top"|"bottom"} [props.placement="bottom"]
 *   Where to anchor the pill relative to the input. Defaults to bottom-
 *   right of the input.
 */
export default function OperatorHintPulse({ inputRef, placement = "bottom" }) {
  const [visible, setVisible] = useState(false);
  const [fading, setFading] = useState(false);
  const fadeTimerRef = useRef(null);
  const hideTimerRef = useRef(null);
  const startedRef = useRef(false);

  useEffect(() => {
    const node = inputRef?.current;
    if (!node) return;

    // Don't hint at users who've already seen it on any input.
    try {
      if (localStorage.getItem(STORAGE_KEY) === "1") return;
    } catch {
      // localStorage unavailable — fail-open.
    }

    let cancelled = false;

    const runTrigger = () => {
      if (cancelled || startedRef.current) return;
      startedRef.current = true;
      try { localStorage.setItem(STORAGE_KEY, "1"); } catch { /* localStorage unavailable — hint may show again; harmless */ }
      setFading(false);
      setVisible(true);
      fadeTimerRef.current = setTimeout(() => {
        if (!cancelled) setFading(true);
      }, DISPLAY_MS - 500);
      hideTimerRef.current = setTimeout(() => {
        if (!cancelled) setVisible(false);
      }, DISPLAY_MS);
    };

    // Defer through setTimeout(0) so StrictMode's double-mount cycle
    // completes before any state writes happen — only the surviving
    // instance's queued trigger actually runs (the first mount's
    // cleanup flips its `cancelled` flag before its timeout fires).
    const fire = () => setTimeout(runTrigger, 0);

    // If the input is already focused at mount (common in palette
    // modals with autoFocus), schedule an immediate trigger.
    if (document.activeElement === node) fire();

    node.addEventListener("focus", fire);
    return () => {
      cancelled = true;
      node.removeEventListener("focus", fire);
    };
  }, [inputRef]);

  if (!visible) return null;

  const positionClass = placement === "top"
    ? "bottom-full mb-2"
    : "top-full mt-2";

  return (
    <div
      className={`absolute right-2 ${positionClass} pointer-events-none z-40`}
      style={{
        opacity: fading ? 0 : 1,
        transition: "opacity 480ms ease-out",
      }}
      data-testid="operator-hint-pulse"
      aria-hidden="true"
    >
      <div
        className="mmntm-operator-hint relative flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-medium shadow-md"
        style={{
          background: "var(--tm-bg-card)",
          border: "1px solid var(--tm-accent)",
          color: "var(--tm-text-primary)",
        }}
      >
        <span style={{ color: "var(--tm-text-secondary)" }}>Press</span>
        <kbd
          className="px-1 py-0.5 rounded font-mono text-[10px] font-bold"
          style={{
            background: "var(--tm-bg-tertiary, var(--tm-bg-secondary))",
            border: "1px solid var(--tm-border-color)",
            color: "var(--tm-accent)",
          }}
        >
          :
        </kbd>
        <span style={{ color: "var(--tm-text-secondary)" }}>for filters</span>

        {/* Pulsing accent ring (purely decorative) */}
        <span
          aria-hidden="true"
          style={{
            position: "absolute",
            inset: 0,
            borderRadius: "9999px",
            border: "2px solid var(--tm-accent)",
            animation: "mmntm-operator-hint-pulse 1400ms ease-out infinite",
            pointerEvents: "none",
          }}
        />
      </div>

      <style>{`
        @keyframes mmntm-operator-hint-pulse {
          0%   { opacity: 0.55; transform: scale(1); }
          70%  { opacity: 0;    transform: scale(1.25); }
          100% { opacity: 0;    transform: scale(1.25); }
        }
      `}</style>
    </div>
  );
}
